/**
 * Theme showcase — per-theme identity element mounted at <body> level.
 *
 * - A single <aside id="theme-showcase"> persists across all screens.
 * - When the menu screen is visible, CSS (:has in theme-showcase.css)
 *   expands the aside to a full hero; everywhere else it's a slim band.
 * - Each theme contributes a small HTML template; the renderer swaps
 *   the template on theme change (via the retroplay:theme-changed event
 *   dispatched by modules/theme-ui.js).
 *
 * Adding a new theme's showcase: add an entry to SHOWCASES below, then
 * (optionally) add per-theme override rules in public/styles/themes.css.
 */

import { getTheme } from '../theme.js';

function _swissDate() {
  const d = new Date();
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Zurich',
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
  });
  const p = Object.fromEntries(fmt.formatToParts(d).map(x => [x.type, x.value]));
  return `${p.weekday.toUpperCase()} · ${p.day} ${p.month.toUpperCase()} ${p.year}`;
}

// ─── Jack-o'-Lantern pixel sprites ────────────────────────────────
// Palette-indexed rows: rind a (outline) → f (highlight), stem g–i,
// carved light j (core) k l (edge). Generated with lobe shading and a
// warm ring around every cut, then hand-checked at 6x. _hwSvg() turns
// each row into horizontal runs, one <path> per colour.
const HW_PALETTE = {
  a: '#260B05', b: '#4E1906', c: '#80300C', d: '#B34D17', e: '#DD6D24', f: '#F59245',
  g: '#1C2A0C', h: '#3D5518', i: '#6E8E2B',
  j: '#FFFBE0', k: '#FFE27A', l: '#FFB43C',
};

const HW_SPRITES = {
  // D-pad eye, A/B button eye, START/SELECT teeth standing in the grin.
  controller: [
    '..................hi..............',
    '.................ghi..............',
    '.................ghh..............',
    '................gghh..............',
    '.............aagghhaa.............',
    '........aaaaaaaaaaaaaaaaaa........',
    '......aaabccbabccccbabccbaaa......',
    '.....abacddcbbbccccbbbcccbbca.....',
    '....abacddddbbccddccbbcccdcbca....',
    '...abacdllldcbcddddcbbccddlllca...',
    '..abacddlkldcdcddddccbcddlkkklca..',
    '.abbcddelklccddddddccbbddlkjklcca.',
    '.acccllljjjkkkedddddccddelkkklcda.',
    'abbcdlkkjjjjjkedddddddcddblllbcbba',
    'acbcdlkkjjjkkkeddddddkkkddbbbdbbca',
    'acbbdbbbkjkbbbdddddekjjjkedddcbbca',
    'acbbcddekjkddddddddekjjjkedddcbbca',
    'acbbcddekkkddcdddddekjjjkedddcbbca',
    'acbbcdedbbbdccddddddbkkkbddedcbbca',
    'acbbdekeeedccddddddddbbbeeekedbbca',
    'acbbdekkkkddcddeeeedddddkkkkedbbca',
    '.acbcdkjjjkbcccjjjjbccckjjjkdcbca.',
    '.acbcdkjjjjbcccjjjjbcccjjjjkdcbca.',
    '..acbdbkkjjjjjjjjjjjjjjjjkkbdbca..',
    '...acbdbbjjjjjjjjjjjjjjjjbbdbca...',
    '...aabcddbbbjjjjjjjjjjbbbddcbaa...',
    '.....acbcdddbbbbbbbbbbdddcbca.....',
    '......aabddddddddddddddddbaa......',
    '........aaaaaccddddccaaaaa........',
    '.............aaaaaaaa.............',
  ],
  // Stepped triangle eyes and nose, saw-tooth grin.
  classic: [
    '.............hi.............',
    '............ghi.............',
    '............ghh.............',
    '...........aghh.a...........',
    '......aaaaaaaaaaaaaaaa......',
    '....aaaddcbbccccbbcddaaa....',
    '...aaaddddbbcddcbbddddaaa...',
    '..abaddllccccddccccllddaba..',
    '..abaddlldccddddccdllddaba..',
    '.abaddlkklddddddddlkklddaba.',
    '.abadelkkkddddddddkkkledaba.',
    'abaddkkjjjkddddddkjjjkkddaba',
    'acbdekkkkkkedeedekkkkkkedbca',
    'acbcdbbbbbbdekkedbbbbbbdcbca',
    'acbcddddddddekkeddddddddcbca',
    'acbcdedddddekkkkedddddedcbca',
    'acbdekeeddddbbbbddddeekedbca',
    '.accdkkkkjdeeeeeedjkkkkdcca.',
    '.accdkjjjbcjjjjjjbbjjjkdcca.',
    '..adckkjjbjjjjjjjjbjjkkcda..',
    '..accbbkjjjjjbbjjjjjkbbcca..',
    '...acddbbjjjjbcjjjjbbddca...',
    '....aacddbbbjjjjbbbddcaa....',
    '......aaaaacbbbbcaaaaa......',
    '...........aaaaaa...........',
  ],
  // The glowing cut-out is a ghost; its eyes and mouth are rind.
  ghost: [
    '............hi..........',
    '...........ghh..........',
    '.........a.ghha.........',
    '.....aa.aaaaaaaa.aa.....',
    '...aabcabbddddbbacbaa...',
    '...aabccccdeedccccbaa...',
    '..abbcddcdlllldcddcbba..',
    '.abbcdddllkkkklldddcbba.',
    '.abbcdekkjjjjjjkkedcbba.',
    'abbcddekjkkjjkkjkeddcbba',
    'abbcdekjkbbkkbbkjkedcbba',
    'abbcdekjkdekkedkjkedcbba',
    'acbcdekjjkkkkkkjjkedcbca',
    'acbcdekjjjkbbkjjjkedcbca',
    'acbcdekjjjjeejjjjkedcbca',
    '.accdekjjjjjjjjjjkedcca.',
    '.accdekjjjjjjjjjjkedcca.',
    '..acdejjbjjjbbjjjbdcca..',
    '..abdejbdbjbddbjbddcba..',
    '...aadbacdbddddbadcaa...',
    '.....aa.acdddcca.aa.....',
    '.........aaaaaa.........',
  ],
  // Uncarved: each catches a neighbour's candle on one side.
  small: [
    '.........hi......',
    '........ghh......',
    '......a.gha......',
    '..aaaaaaaabaaaa..',
    '.aacccbdddcdddca.',
    '.abddccdddcdeeda.',
    'abcddccdddddeeeda',
    'abcddccdddddefeda',
    'abdddccdddddefeea',
    'abdddccdedddeffea',
    '.abdddcdeedeffea.',
    '.abdddcdeedeffea.',
    '..aaaacdeddaaaa..',
    '......aaaaa......',
  ],
  tiny: [
    '......hi.....',
    '....a.gha....',
    '.aaacaaabaaa.',
    '.aeeddddcdca.',
    'adeecdddcddba',
    'aefeddddcddca',
    'aefeddedcddca',
    '.affeeeddeda.',
    '.aaaeeeddaaa.',
    '....aaaaa....',
  ],
  // A far-off lantern: only the carved face survives the dark.
  eyes: [
    '.l.....l.',
    'lkl...lkl',
    '.........',
    '.lkkkkkl.',
  ],
};

/**
 * Render palette-indexed pixel rows as a crisp SVG.
 * @param {string[]} rows
 * @param {string} cls
 * @param {{ only?: string, fill?: string }} [opts]  `only` keeps just those
 *   palette keys; `fill` paints them all one colour (the bloom layer).
 */
function _hwSvg(rows, cls, { only = '', fill = '' } = {}) {
  const byColor = {};
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length;) {
      const c = row[x];
      let run = 1;
      while (x + run < row.length && row[x + run] === c) run++;
      if (c !== '.' && (!only || only.includes(c))) {
        const color = fill || HW_PALETTE[c];
        byColor[color] = (byColor[color] || '') + `M${x} ${y}h${run}v1h-${run}z`;
      }
      x += run;
    }
  });
  const paths = Object.entries(byColor).map(([color, d]) => `<path fill="${color}" d="${d}"/>`).join('');
  return `<svg class="${cls}" viewBox="0 0 ${rows[0].length} ${rows.length}" shape-rendering="crispEdges" focusable="false">${paths}</svg>`;
}

/** One pumpkin; lit ones get a flickering bloom and a light pool. */
function _hwPumpkin(name, { lit = false, flick = 3.2, delay = 0 } = {}) {
  const rows = HW_SPRITES[name];
  const style = `--w:${rows[0].length};--h:${rows.length};--hw-flick:${flick}s;--hw-delay:${delay}s`;
  return `<span class="sc-hw-pumpkin sc-hw-${name}" style="${style}">`
    + (lit ? '<i class="sc-hw-pool"></i>' : '')
    + _hwSvg(rows, 'sc-hw-art')
    + (lit ? _hwSvg(rows, 'sc-hw-glow', { only: 'jkl', fill: '#FFE9A6' }) : '')
    + '</span>';
}

// Far-off lanterns: [left %, top %, pixel scale relative to --px, opacity,
// optional haunt delay]. Only a few shift red and drift; the rest stay still.
const HW_FAR = [
  [5, 50, 0.5, 0.34], [13, 64, 0.5, 0.26], [21, 46, 0.75, 0.42, -4], [30, 60, 0.5, 0.24],
  [70, 58, 0.5, 0.26], [78, 44, 0.75, 0.4, -11], [87, 62, 0.5, 0.28], [95, 48, 0.75, 0.36, -17],
];

function _hwFarFace([x, y, s, opacity, delay]) {
  const haunts = delay !== undefined;
  const cls = `sc-hw-eyes${haunts ? ' sc-hw-haunt' : ''}`;
  const style = `left:${x}%;top:${y}%;--s:${s};opacity:${opacity}`
    + (haunts ? `;--hw-haunt-delay:${delay}s;--hw-drift-x:${x < 50 ? 5 : -5}px` : '');
  return `<span class="${cls}" style="${style}">`
    + _hwSvg(HW_SPRITES.eyes, 'sc-hw-eyes-art')
    + (haunts ? _hwSvg(HW_SPRITES.eyes, 'sc-hw-eyes-red', { fill: '#FF4B55' }) : '')
    + '</span>';
}

/** @type {Record<string, () => string>} */
const SHOWCASES = {
  // ─── Wasteland (Fallout Pip-Boy 3000 Mk IV) ──────────────────────
  // Based on the physical Bethesda Pip-Boy replica: dark gray chassis,
  // left-wing with gauge dial + "Pip-Boy" plate + chrome wheel, center
  // screen bezel with corner rivets + amber STATS/ITEMS/DATA buttons
  // beneath, and a rubber accordion connector on the right.
  wasteland: () => `
    <div class="sc-wasteland" aria-hidden="true">
      <div class="sc-panel">
        <div class="sc-pipboy">
          <div class="sc-pipboy-left">
            <div class="sc-pipboy-gauge">
              <span class="sc-pipboy-gauge-ticks"></span>
              <span class="sc-pipboy-gauge-needle"></span>
            </div>
            <div class="sc-pipboy-plate">Pip-Boy</div>
            <div class="sc-pipboy-wheel"></div>
          </div>
          <div class="sc-pipboy-center">
            <div class="sc-pipboy-bezel">
              <span class="sc-pipboy-rivet sc-pipboy-rivet-tl"></span>
              <span class="sc-pipboy-rivet sc-pipboy-rivet-tr"></span>
              <span class="sc-pipboy-rivet sc-pipboy-rivet-bl"></span>
              <span class="sc-pipboy-rivet sc-pipboy-rivet-br"></span>
              <div class="sc-wasteland-screen">
                <div class="sc-wasteland-header">
                  <span>STATS</span>
                  <span class="sc-wasteland-header-meta">LVL 14 &middot; HP 245/245 &middot; AP 98/98</span>
                </div>
                <div class="sc-wasteland-body">
                  <svg class="sc-pipboy-vboy" viewBox="0 0 48 64" aria-hidden="true" fill="none" stroke="#7AE865" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="24" cy="12" r="8"/>
                    <circle cx="20" cy="11" r="0.7" fill="#7AE865"/>
                    <circle cx="28" cy="11" r="0.7" fill="#7AE865"/>
                    <path d="M20 15 Q24 17 28 15"/>
                    <path d="M15 23 L33 23 L34 42 L14 42 Z"/>
                    <line x1="19" y1="23" x2="19" y2="42"/>
                    <line x1="29" y1="23" x2="29" y2="42"/>
                    <path d="M15 25 L6 34 L9 41"/>
                    <path d="M33 25 L43 16 L43 9"/>
                    <circle cx="43" cy="9" r="2.2"/>
                    <line x1="18" y1="42" x2="14" y2="58"/>
                    <line x1="30" y1="42" x2="34" y2="58"/>
                  </svg>
                  <div class="sc-wasteland-stats">
                    <div class="sc-wasteland-stat"><em>HP</em><div class="sc-wasteland-bar"><i style="width:100%"></i></div><b>245</b></div>
                    <div class="sc-wasteland-stat"><em>AP</em><div class="sc-wasteland-bar"><i style="width:84%"></i></div><b>98</b></div>
                    <div class="sc-wasteland-stat"><em>RAD</em><div class="sc-wasteland-bar sc-wasteland-bar-rad"><i style="width:4%"></i></div><b>0</b></div>
                    <div class="sc-wasteland-stat"><em>CAPS</em><div class="sc-wasteland-bar sc-wasteland-bar-amber"><i style="width:42%"></i></div><b>142</b></div>
                  </div>
                </div>
                <div class="sc-wasteland-tabs">
                  <span>Status</span>
                  <span>S.P.E.C.I.A.L.</span>
                  <span>Skills</span>
                  <span class="sc-wasteland-tab-active">Perks</span>
                  <span>General</span>
                </div>
              </div>
            </div>
            <div class="sc-pipboy-buttons">
              <span class="sc-pipboy-btn">STATS</span>
              <span class="sc-pipboy-btn">ITEMS</span>
              <span class="sc-pipboy-btn">DATA</span>
            </div>
          </div>
          <div class="sc-pipboy-right">
            <span class="sc-pipboy-boot"></span>
          </div>
        </div>
      </div>
    </div>
  `,

  // ─── Aurora (default) — aurora borealis + observatory readout ────
  default: () => `
    <div class="sc-default" aria-hidden="true">
      <div class="sc-aurora-stars"></div>
      <div class="sc-aurora-band sc-aurora-band-1"></div>
      <div class="sc-aurora-band sc-aurora-band-2"></div>
      <div class="sc-aurora-band sc-aurora-band-3"></div>
      <div class="sc-aurora-rays"></div>
      <div class="sc-aurora-horizon"></div>
      <div class="sc-aurora-panel">
        <div class="sc-aurora-title">Player One Ready</div>
        <div class="sc-aurora-coords">ARCADE NET-7 · INSERT COIN · 2-PLAYER MODE</div>
        <div class="sc-aurora-readout">
          <span class="sc-aurora-pill"><em>High Score</em><b>999,999</b></span>
          <span class="sc-aurora-pill"><em>Level</em><b>1-1</b></span>
          <span class="sc-aurora-pill sc-aurora-pill-active"><em>Status</em><b>● READY</b></span>
          <span class="sc-aurora-pill"><em>Lives</em><b>♥ ♥ ♥</b></span>
        </div>
      </div>
    </div>
  `,

  // ─── Trinitron — broadcast console face ──────────────────────────
  trinitron: () => `
    <div class="sc-trinitron" aria-hidden="true">
      <div class="sc-smpte"></div>
      <div class="sc-panel">
        <div class="sc-knob-group sc-trinitron-knob sc-trinitron-knob-ch">
          <div class="sc-knob" style="--sc-knob-angle: 150deg"></div>
          <div class="sc-label">Channel</div>
        </div>
        <div class="sc-trinitron-stack">
          <div class="sc-screen sc-trinitron-readout">CH 03</div>
          <div class="sc-trinitron-leds">
            <span class="sc-led pulse" style="--sc-led-color: #3BE08A"></span>
            <span class="sc-label">On Air</span>
          </div>
        </div>
        <div class="sc-knob-group sc-trinitron-knob sc-trinitron-knob-vol">
          <div class="sc-knob" style="--sc-knob-angle: 210deg"></div>
          <div class="sc-label">Volume</div>
        </div>
      </div>
    </div>
  `,

  // ─── Phosphor — terminal boot log ────────────────────────────────
  phosphor: () => `
    <div class="sc-phosphor" aria-hidden="true">
      <pre class="sc-phosphor-log">RETROPLAY v2.7.1 :: PHOSPHOR BOOT
LOADING CORES................ OK
LINK: STABLE   RTT: 18 ms     FPS: 60.0
USER: retrokid_84
&gt; _</pre>
    </div>
  `,

  // ─── Matrix — falling code columns ───────────────────────────────
  matrix: () => `
    <div class="sc-matrix" aria-hidden="true">
      <div class="sc-matrix-cols">
        ${Array.from({ length: 24 }, (_, i) => {
          const delay = -(Math.random() * 10).toFixed(2);
          const dur = (4 + Math.random() * 5).toFixed(2);
          const op = (0.5 + Math.random() * 0.45).toFixed(2);
          return `<span class="sc-matrix-col c${i}" style="animation-delay:${delay}s;animation-duration:${dur}s;opacity:${op}"></span>`;
        }).join('')}
      </div>
      <div class="sc-matrix-cursor">
        <span class="sc-matrix-line sc-matrix-line-1"><span class="sc-matrix-text">Wake up, Neo...</span></span>
        <span class="sc-matrix-line sc-matrix-line-2"><span class="sc-matrix-text">The Matrix has you...</span></span>
        <span class="sc-matrix-line sc-matrix-line-3"><span class="sc-matrix-text">Follow the white rabbit.</span></span>
        <span class="sc-cursor"></span>
      </div>
    </div>
  `,

  // ─── Cyberpunk — Blade Runner street bitmap as the hero backdrop.
  // Uses public/img/cyberpunk.png directly. A vignette gradient and
  // the rain animation overlay the photo for legibility + motion;
  // the SECTOR 7 // NEO TOKYO 2099 banner sits at the bottom edge.
  cyberpunk: () => `
    <div class="sc-cyberpunk" aria-hidden="true">
      <div class="sc-cp-photo"></div>
      <div class="sc-cp-vignette"></div>
      <div class="sc-cp-banner">
        <span class="sc-cp-banner-zone">SECTOR 7</span>
        <span class="sc-cp-banner-div">//</span>
        <span class="sc-cp-banner-city">NEO&nbsp;TOKYO&nbsp;2099</span>
      </div>
    </div>
  `,

  // ─── Cartridge Print — three recognisable cartridge SVGs ─────────
  // Left to right: NES (tall gray with ribbed vent + Nintendo label),
  // Game Boy (smaller gray with GAME BOY header), Atari 2600 (wide
  // black with coloured top label). Each sits in its own .sc-cart
  // wrapper so CSS can rotate + overlap them.
  cartridge: () => `
    <div class="sc-cartridge" aria-hidden="true">
      <div class="sc-panel">
        <div class="sc-cart sc-cart-nes">
          <svg viewBox="0 0 100 130" fill="none" aria-hidden="true">
            <path d="M 6 8 L 6 4 L 14 4 L 14 8 L 86 8 L 86 4 L 94 4 L 94 8
                     L 94 104 L 88 104 L 88 110 L 82 110 L 82 104 L 78 104
                     L 78 122 L 22 122 L 22 104 L 18 104 L 18 110 L 12 110
                     L 12 104 L 6 104 Z"
                  fill="#8A8C96" stroke="#14141F" stroke-width="1.3"
                  stroke-linejoin="round"/>
            <rect x="12" y="14" width="28" height="86" fill="#5F6169"/>
            <g stroke="#14141F" stroke-width="0.5" opacity="0.85">
              <line x1="12" y1="17" x2="40" y2="17"/>
              <line x1="12" y1="21" x2="40" y2="21"/>
              <line x1="12" y1="25" x2="40" y2="25"/>
              <line x1="12" y1="29" x2="40" y2="29"/>
              <line x1="12" y1="33" x2="40" y2="33"/>
              <line x1="12" y1="37" x2="40" y2="37"/>
              <line x1="12" y1="41" x2="40" y2="41"/>
              <line x1="12" y1="45" x2="40" y2="45"/>
              <line x1="12" y1="49" x2="40" y2="49"/>
              <line x1="12" y1="53" x2="40" y2="53"/>
              <line x1="12" y1="57" x2="40" y2="57"/>
              <line x1="12" y1="61" x2="40" y2="61"/>
              <line x1="12" y1="65" x2="40" y2="65"/>
              <line x1="12" y1="69" x2="40" y2="69"/>
              <line x1="12" y1="73" x2="40" y2="73"/>
              <line x1="12" y1="77" x2="40" y2="77"/>
              <line x1="12" y1="81" x2="40" y2="81"/>
              <line x1="12" y1="85" x2="40" y2="85"/>
              <line x1="12" y1="89" x2="40" y2="89"/>
              <line x1="12" y1="93" x2="40" y2="93"/>
              <line x1="12" y1="97" x2="40" y2="97"/>
            </g>
            <rect x="44" y="14" width="46" height="86" fill="#0E0E18"
                  stroke="#14141F" stroke-width="0.8"/>
            <rect x="45" y="84" width="44" height="15" fill="#D62828"/>
            <text x="67" y="94" font-family="'Press Start 2P', monospace"
                  font-size="4" fill="#FFF9EA" text-anchor="middle"
                  letter-spacing="0.3">NINTENDO</text>
            <rect x="50" y="20" width="34" height="56" fill="#1C6BB8"
                  opacity="0.75"/>
            <circle cx="60" cy="36" r="4" fill="#D9A441"/>
            <rect x="54" y="46" width="26" height="6" fill="#FFF9EA"
                  opacity="0.85"/>
            <rect x="54" y="56" width="26" height="6" fill="#FFF9EA"
                  opacity="0.55"/>
            <rect x="54" y="66" width="16" height="6" fill="#FFF9EA"
                  opacity="0.55"/>
            <polygon points="47,110 50,116 53,110" fill="#14141F"/>
          </svg>
        </div>

        <div class="sc-cart sc-cart-gb">
          <svg viewBox="0 0 80 100" fill="none" aria-hidden="true">
            <path d="M 4 4 Q 4 2 6 2 L 74 2 Q 76 2 76 4 L 76 84
                     L 70 84 L 70 92 L 60 92 L 60 96 L 20 96 L 20 92
                     L 10 92 L 10 84 L 4 84 Z"
                  fill="#BEBEC5" stroke="#14141F" stroke-width="1.3"
                  stroke-linejoin="round"/>
            <rect x="6" y="4" width="68" height="10" fill="#1A1A24"/>
            <text x="40" y="11.5" font-family="Arial, Helvetica, sans-serif"
                  font-weight="800" font-style="italic" font-size="5.2"
                  fill="#FFF9EA" text-anchor="middle">GAME BOY</text>
            <rect x="10" y="18" width="60" height="60" fill="#F3E7CF"
                  stroke="#14141F" stroke-width="0.6"/>
            <rect x="14" y="22" width="52" height="22" fill="#2A2A38"/>
            <text x="40" y="37" font-family="'Press Start 2P', monospace"
                  font-size="7" fill="#D9A441" text-anchor="middle"
                  letter-spacing="1">TETRIS</text>
            <g>
              <rect x="20" y="50" width="6" height="6" fill="#D62828"/>
              <rect x="26" y="50" width="6" height="6" fill="#1C6BB8"/>
              <rect x="32" y="50" width="6" height="6" fill="#2A7A2A"/>
              <rect x="38" y="50" width="6" height="6" fill="#D9A441"/>
              <rect x="26" y="56" width="6" height="6" fill="#1C6BB8"/>
              <rect x="32" y="56" width="6" height="6" fill="#2A7A2A"/>
              <rect x="44" y="56" width="6" height="6" fill="#D62828"/>
              <rect x="32" y="62" width="6" height="6" fill="#2A7A2A"/>
              <rect x="38" y="62" width="6" height="6" fill="#D9A441"/>
              <rect x="44" y="62" width="6" height="6" fill="#D62828"/>
            </g>
            <rect x="72" y="20" width="3" height="56" fill="#1A1A24"/>
          </svg>
        </div>

        <div class="sc-cart sc-cart-atari">
          <svg viewBox="0 0 120 84" fill="none" aria-hidden="true">
            <defs>
              <radialGradient id="sc-atari-crt" cx="50%" cy="50%" r="65%">
                <stop offset="0%"  stop-color="#153C70"/>
                <stop offset="60%" stop-color="#061A3A"/>
                <stop offset="100%" stop-color="#020612"/>
              </radialGradient>
              <linearGradient id="sc-atari-wood" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stop-color="#8A5028"/>
                <stop offset="35%" stop-color="#A3653A"/>
                <stop offset="70%" stop-color="#6A3F1E"/>
                <stop offset="100%" stop-color="#3A1F0C"/>
              </linearGradient>
              <radialGradient id="sc-atari-knob" cx="30%" cy="28%" r="75%">
                <stop offset="0%"  stop-color="#F4EFE2"/>
                <stop offset="55%" stop-color="#A69C8A"/>
                <stop offset="100%" stop-color="#2A2218"/>
              </radialGradient>
              <clipPath id="sc-atari-crt-clip">
                <rect x="22" y="24" width="48" height="36" rx="5"/>
              </clipPath>
            </defs>

            <path d="M 4 6 Q 4 4 6 4 L 114 4 Q 116 4 116 6 L 116 74
                     Q 116 76 114 76 L 100 76 L 100 80 L 20 80 L 20 76
                     L 6 76 Q 4 76 4 74 Z"
                  fill="#1A1A24" stroke="#14141F" stroke-width="1.3"
                  stroke-linejoin="round"/>
            <rect x="10" y="10" width="100" height="5" fill="#2A2A38"/>
            <text x="60" y="14" font-family="'Press Start 2P', monospace"
                  font-size="2.8" fill="#D9A441" text-anchor="middle"
                  letter-spacing="0.6">ATARI</text>

            <rect x="10" y="18" width="100" height="48" rx="4"
                  fill="url(#sc-atari-wood)" stroke="#2A1608"
                  stroke-width="0.6"/>
            <g stroke="#2A1608" stroke-width="0.35" opacity="0.45" fill="none">
              <line x1="12" y1="24" x2="108" y2="24"/>
              <line x1="12" y1="30" x2="108" y2="30"/>
              <line x1="12" y1="36" x2="108" y2="36"/>
              <line x1="12" y1="42" x2="108" y2="42"/>
              <line x1="12" y1="50" x2="108" y2="50"/>
              <line x1="12" y1="58" x2="108" y2="58"/>
              <line x1="12" y1="62" x2="108" y2="62"/>
            </g>
            <g fill="#2A1608" opacity="0.25">
              <ellipse cx="22" cy="44" rx="5" ry="1.4"/>
              <ellipse cx="98" cy="26" rx="4" ry="1.2"/>
              <ellipse cx="104" cy="60" rx="3" ry="1"/>
            </g>
            <rect x="12" y="19" width="96" height="0.8"
                  fill="rgba(255,230,180,0.35)"/>

            <rect x="18" y="22" width="56" height="40" rx="3"
                  fill="#0A0A12" stroke="#14141F" stroke-width="0.5"/>
            <rect x="22" y="24" width="48" height="36" rx="5"
                  fill="url(#sc-atari-crt)"/>

            <g clip-path="url(#sc-atari-crt-clip)">
              <g fill="#FFF9EA">
                <circle cx="25" cy="27" r="0.6"/>
                <circle cx="33" cy="32" r="0.5"/>
                <circle cx="43" cy="26" r="0.6"/>
                <circle cx="56" cy="35" r="0.5"/>
                <circle cx="64" cy="29" r="0.6"/>
                <circle cx="68" cy="44" r="0.5"/>
                <circle cx="28" cy="50" r="0.5"/>
                <circle cx="52" cy="55" r="0.5"/>
                <circle cx="36" cy="57" r="0.5"/>
                <circle cx="65" cy="53" r="0.5"/>
              </g>
              <g fill="none" stroke="#FFF9EA" stroke-width="0.5"
                 stroke-linejoin="round">
                <polygon points="30,37 33,35 36,36 37,40 35,44 31,42"/>
                <polygon points="59,42 64,40 67,43 65,48 60,49 57,45"/>
                <polygon points="40,52 43,50 46,53 44,57 40,56"/>
              </g>
              <polygon points="46,39 49,45 46,44 43,45"
                       fill="none" stroke="#FFF9EA" stroke-width="0.7"
                       stroke-linejoin="round"/>
              <g stroke="rgba(0,0,0,0.45)" stroke-width="0.35">
                <line x1="20" y1="26" x2="72" y2="26"/>
                <line x1="20" y1="29" x2="72" y2="29"/>
                <line x1="20" y1="32" x2="72" y2="32"/>
                <line x1="20" y1="35" x2="72" y2="35"/>
                <line x1="20" y1="38" x2="72" y2="38"/>
                <line x1="20" y1="41" x2="72" y2="41"/>
                <line x1="20" y1="44" x2="72" y2="44"/>
                <line x1="20" y1="47" x2="72" y2="47"/>
                <line x1="20" y1="50" x2="72" y2="50"/>
                <line x1="20" y1="53" x2="72" y2="53"/>
                <line x1="20" y1="56" x2="72" y2="56"/>
                <line x1="20" y1="59" x2="72" y2="59"/>
              </g>
              <ellipse cx="35" cy="31" rx="11" ry="5.5"
                       fill="rgba(255,255,255,0.07)"/>
            </g>

            <path d="M 24 26 Q 25 25 28 25 L 64 25 Q 67 25 68 26"
                  fill="none" stroke="rgba(255,255,255,0.2)"
                  stroke-width="0.35"/>

            <circle cx="88" cy="30" r="5"
                    fill="url(#sc-atari-knob)"
                    stroke="#14141F" stroke-width="0.4"/>
            <circle cx="88" cy="30" r="1.8" fill="#14141F"/>
            <line x1="88" y1="27" x2="88" y2="25.5"
                  stroke="#F4EFE2" stroke-width="0.5"
                  stroke-linecap="round"/>
            <text x="88" y="23" font-family="'Press Start 2P', monospace"
                  font-size="1.8" fill="#F4EFE2" text-anchor="middle"
                  opacity="0.8">CH</text>

            <circle cx="88" cy="50" r="5"
                    fill="url(#sc-atari-knob)"
                    stroke="#14141F" stroke-width="0.4"/>
            <circle cx="88" cy="50" r="1.8" fill="#14141F"/>
            <line x1="88" y1="50" x2="91.2" y2="52.2"
                  stroke="#F4EFE2" stroke-width="0.5"
                  stroke-linecap="round"/>
            <text x="88" y="59.5" font-family="'Press Start 2P', monospace"
                  font-size="1.8" fill="#F4EFE2" text-anchor="middle"
                  opacity="0.8">VOL</text>

            <circle cx="100" cy="30" r="1.1" fill="#FF4A50"/>
            <circle cx="100" cy="30" r="0.35"
                    fill="#FFF9EA" opacity="0.7"/>
            <g fill="#2A1608" opacity="0.55">
              <circle cx="96" cy="50" r="0.5"/>
              <circle cx="99" cy="50" r="0.5"/>
              <circle cx="102" cy="50" r="0.5"/>
              <circle cx="96" cy="53" r="0.5"/>
              <circle cx="99" cy="53" r="0.5"/>
              <circle cx="102" cy="53" r="0.5"/>
              <circle cx="96" cy="56" r="0.5"/>
              <circle cx="99" cy="56" r="0.5"/>
              <circle cx="102" cy="56" r="0.5"/>
            </g>

            <rect x="10" y="67" width="100" height="8" fill="#D9A441"/>
            <text x="60" y="73" font-family="'Press Start 2P', monospace"
                  font-size="4" fill="#14141F" text-anchor="middle"
                  letter-spacing="0.8">ASTEROIDS</text>
          </svg>
        </div>

      </div>
    </div>
  `,

  // ─── Game Boy — DMG hardware photo + LCD status readout ──────────
  gameboy: () => `
    <div class="sc-gameboy" aria-hidden="true">
      <div class="sc-panel sc-gameboy-panel">
        <div class="sc-gameboy-hero">
          <img src="/img/game-boy-dmg.png" alt="" loading="eager" decoding="async">
        </div>
        <div class="sc-gameboy-lcd-wrap">
          <div class="sc-screen sc-gameboy-lcd">
            <span class="sc-gameboy-lcd-line">▶ RETROPLAY</span>
            <span class="sc-gameboy-lcd-line sc-gameboy-lcd-dim">READY</span>
          </div>
        </div>
        <div class="sc-gameboy-copy">
          <span class="sc-gameboy-brand">GAME BOY</span>
          <span class="sc-gameboy-meta">DMG-01 · LINK READY · 4 SHADE LCD</span>
        </div>
      </div>
    </div>
  `,

  // ─── Neon Arcade — marquee with coin slot ─────────────────────────
  arcade: () => `
    <div class="sc-arcade" aria-hidden="true">
      <div class="sc-panel">
        <div class="sc-arcade-marquee">HI-SCORE</div>
        <div class="sc-arcade-coin">
          <div class="sc-arcade-coin-slot"></div>
          <div class="sc-label">Insert Coin</div>
        </div>
        <div class="sc-arcade-credits">
          <span class="sc-label">Credits</span>
          <span class="sc-screen sc-arcade-credits-val">03</span>
        </div>
      </div>
    </div>
  `,

  // ─── Modular Panel — rack unit: knobs + VU + readout ──────────────
  modular: () => `
    <div class="sc-modular" aria-hidden="true">
      <span class="sc-screw sc-screw-tl"></span>
      <span class="sc-screw sc-screw-tr"></span>
      <span class="sc-screw sc-screw-bl"></span>
      <span class="sc-screw sc-screw-br"></span>
      <div class="sc-panel">
        <div class="sc-knob-group">
          <div class="sc-knob" style="--sc-knob-angle: 90deg"></div>
          <div class="sc-label">Gain</div>
        </div>
        <div class="sc-knob-group">
          <div class="sc-knob" style="--sc-knob-angle: 170deg"></div>
          <div class="sc-label">Freq</div>
        </div>
        <div class="sc-modular-readout">
          <div class="sc-screen sc-modular-screen">127.0</div>
          <div class="sc-vu">
            ${Array.from({ length: 12 }, (_, i) => `<i class="${i < 7 ? 'on' : ''}"></i>`).join('')}
          </div>
        </div>
        <div class="sc-knob-group">
          <div class="sc-knob" style="--sc-knob-angle: 220deg"></div>
          <div class="sc-label">Res</div>
        </div>
      </div>
    </div>
  `,

  // ─── Neo Swiss — editorial masthead ──────────────────────────────
  swiss: () => `
    <div class="sc-swiss" aria-hidden="true">
      <div class="sc-swiss-rule sc-swiss-rule-top"></div>
      <div class="sc-panel">
        <div class="sc-swiss-num">N° 04</div>
        <div class="sc-swiss-title">ISSUE<br><span class="sc-swiss-title-accent">RETROPLAY</span><br>VOL. 26</div>
        <div class="sc-swiss-date">${_swissDate()}</div>
      </div>
      <div class="sc-swiss-rule sc-swiss-rule-bot"></div>
    </div>
  `,

  woodgrain: () => `
    <div class="sc-woodgrain" aria-hidden="true">
      <div class="sc-woodgrain-asteroids">
        <div class="sc-asteroid sc-asteroid-0"></div>
        <div class="sc-asteroid sc-asteroid-1"></div>
        <div class="sc-asteroid sc-asteroid-2"></div>
        <div class="sc-asteroid sc-asteroid-3"></div>
        <div class="sc-asteroid sc-asteroid-4"></div>
        <div class="sc-asteroid sc-asteroid-5"></div>
        <div class="sc-asteroid sc-asteroid-6"></div>
        <div class="sc-asteroid sc-asteroid-7"></div>
        <div class="sc-asteroid sc-asteroid-8"></div>
        <div class="sc-asteroid sc-asteroid-9"></div>
        <div class="sc-target sc-target-0"></div>
        <div class="sc-target sc-target-1"></div>
        <div class="sc-target sc-target-2"></div>
        <div class="sc-ship"></div>
        <div class="sc-bullet sc-bullet-0"></div>
        <div class="sc-bullet sc-bullet-1"></div>
        <div class="sc-bullet sc-bullet-2"></div>
      </div>
      <div class="sc-panel">
        <div class="sc-woodgrain-hero">
          <img src="/img/atari-2600.png" alt="" draggable="false">
        </div>
      </div>
      <div class="sc-woodgrain-bar">
        <div class="sc-woodgrain-bar-wood"></div>
        <div class="sc-woodgrain-bar-panel">
          <svg class="sc-fuji" viewBox="0 0 20 20" fill="none">
            <g fill="currentColor">
              <path d="M 0 20 Q 0 8 5 1 L 7 1 Q 3.2 8 3.2 20 Z"/>
              <rect x="8.8" y="1" width="2.4" height="19"/>
              <path d="M 20 20 Q 20 8 15 1 L 13 1 Q 16.8 8 16.8 20 Z"/>
            </g>
          </svg>
          <span class="sc-woodgrain-bar-label">VIDEO COMPUTER SYSTEM</span>
          <svg class="sc-fuji" viewBox="0 0 20 20" fill="none">
            <g fill="currentColor">
              <path d="M 0 20 Q 0 8 5 1 L 7 1 Q 3.2 8 3.2 20 Z"/>
              <rect x="8.8" y="1" width="2.4" height="19"/>
              <path d="M 20 20 Q 20 8 15 1 L 13 1 Q 16.8 8 16.8 20 Z"/>
            </g>
          </svg>
        </div>
      </div>
      <div class="sc-woodgrain-stripe"></div>
    </div>
  `,

  frontloader: () => `
    <div class="sc-frontloader" aria-hidden="true">
      <div class="sc-frontloader-stripe"></div>
      <div class="sc-panel">
        <div class="sc-frontloader-hero">
          <img src="/img/nes.png" alt="" draggable="false">
        </div>
      </div>
      <div class="sc-frontloader-bar">
        <div class="sc-frontloader-bar-inner">
          <span class="sc-frontloader-bar-led"></span>
          <span class="sc-frontloader-bar-label">ENTERTAINMENT SYSTEM</span>
          <span class="sc-frontloader-bar-led"></span>
        </div>
      </div>
    </div>
  `,

  // ─── Super Power (SNES) — console hero image + wordmark ────────────
  super: () => `
    <div class="sc-super" aria-hidden="true">
      <div class="sc-panel">
        <div class="sc-super-hero">
          <img src="/img/snes.png" alt="" draggable="false">
        </div>
      </div>
      <div class="sc-super-bar">
        <span class="sc-super-wordmark">SUPER</span>
        <span class="sc-label">Now You're Playing With Super Power</span>
      </div>
    </div>
  `,

  // ─── Blueprint — console schematic hero + drafting title block ────
  blueprint: () => {
    const d = new Date();
    const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const date = `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
    return `
    <div class="sc-blueprint" aria-hidden="true">
      <div class="sc-panel">
        <div class="sc-blueprint-hero">
          <img class="sc-bp-snes" src="/img/snes-blueprint.png" alt="" draggable="false">
          <img class="sc-bp-nes" src="/img/nes-blueprint.png" alt="" draggable="false">
          <img class="sc-bp-atari" src="/img/atari-blueprint.png" alt="" draggable="false">
          <img class="sc-bp-gb" src="/img/gameboy.png" alt="" draggable="false">
        </div>
        <div class="sc-blueprint-block">
          <div class="sc-blueprint-row"><span class="sc-label">DWG N°</span><b>0001</b></div>
          <div class="sc-blueprint-row"><span class="sc-label">TITLE</span><b>RETROPLAY</b></div>
          <div class="sc-blueprint-row"><span class="sc-label">SHEET</span><b>1 / 1</b></div>
          <div class="sc-blueprint-row"><span class="sc-label">DATE</span><b>${date}</b></div>
          <div class="sc-blueprint-row"><span class="sc-label">SCALE</span><b>1:1</b></div>
        </div>
      </div>
      <div class="sc-blueprint-stamp">APPROVED</div>
    </div>
  `;
  },

  // ─── Steampunk — hero image showcase ─────────────────────────────
  // Randomly picks one of two steampunk hero images per page load.
  // The 5-gear SVG mechanism is stashed for later re-integration.
  steampunk: () => {
    const img = Math.random() < 0.5 ? 'steampunk1.png' : 'steampunk2.png';
    // Expose the chosen image as a CSS custom property so the body
    // background can reference the same random pick.
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty(
        '--steam-hero', `url(/img/${img})`,
      );
    }
    return `
    <div class="sc-steampunk" aria-hidden="true">
      <div class="sc-panel">
        <div class="sc-steam-hero">
          <img src="/img/${img}" alt="" draggable="false"/>
        </div>
        <div class="sc-steam-nameplate">RETROPLAY</div>
      </div>
    </div>
  `;
  },

  // ─── Jack-o'-Lantern — pumpkin patch lit from within ─────────────
  // Home: carved lanterns on a porch step with far-off faces in the
  // dark. Sub-pages: CSS swaps in a string of pumpkin lights instead.
  halloween: () => `
    <div class="sc-halloween" aria-hidden="true">
      <div class="sc-hw-far">
        ${HW_FAR.map(_hwFarFace).join('')}
      </div>
      <div class="sc-hw-fog"></div>
      <div class="sc-hw-ground"></div>
      <div class="sc-hw-patch">
        ${_hwPumpkin('small')}
        ${_hwPumpkin('classic', { lit: true, flick: 2.9, delay: -1.1 })}
        ${_hwPumpkin('controller', { lit: true, flick: 3.6, delay: -0.3 })}
        ${_hwPumpkin('ghost', { lit: true, flick: 3.1, delay: -2 })}
        ${_hwPumpkin('tiny')}
      </div>
      <div class="sc-hw-garland"></div>
    </div>
  `,

  // ─── Hand Turkey — the family's traced hands on a bulletin board ──
  // Home: four drawings, a list and four leaves. Sub-pages: a paper
  // chain. All of it lives in themes.css: the eager bundle has no room.
  thanksgiving: () => '<div class="sc-thanksgiving" aria-hidden="true"><div class="sc-tg-board">'
    + '<i></i>'.repeat(9) + '</div></div>',
};

/**
 * Swap the showcase template into the #theme-showcase mount.
 * Unknown ids fall back to the default template so new themes in the
 * catalog don't leave the aside empty before their showcase ships.
 *
 * @param {string} [themeId]  theme id; defaults to the currently-applied theme
 */
export function renderShowcase(themeId) {
  if (typeof document === 'undefined') return;
  const mount = document.getElementById('theme-showcase');
  if (!mount) return;
  const id = themeId || getTheme();
  const tpl = SHOWCASES[id] || SHOWCASES.default;
  mount.innerHTML = tpl();
  mount.dataset.showcase = id;
}

// Track whether bindThemeShowcase has been called — double-binding is a
// no-op so main.js can call it safely without guarding.
let _bound = false;

/**
 * Mount the showcase for the current theme and subscribe to future
 * theme changes. Idempotent — safe to call twice.
 *
 * @param {*} _app  the NESPlay instance (unused, accepted for parity
 *                  with other bindX(app) modules)
 */
export function bindThemeShowcase(/* _app */) {
  renderShowcase();
  if (_bound) return;
  _bound = true;
  if (typeof window === 'undefined') return;
  window.addEventListener('retroplay:theme-changed', (ev) => {
    renderShowcase(ev?.detail?.id);
  });
}

// Test-only hooks — keep the private template registry accessible so
// tests can assert the template set without exposing mutation.
export const __test__ = {
  getTemplate: (id) => SHOWCASES[id] || SHOWCASES.default,
  templateIds: () => Object.keys(SHOWCASES),
  resetBinding: () => { _bound = false; },
  hwSprites: () => HW_SPRITES,
  hwPalette: () => HW_PALETTE,
};
